import { useCallback, useEffect, useState } from "react";
import { Check, Download, Pencil, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/api";
import { hindiError, hindiFeedback } from "@/lib/adminFeedback";
import { filterRank } from "@/lib/adminSearch";

function exportCSV(data, filename) {
  if (!data.length) return toast.error("No data to export");
  const headers = Object.keys(data[0]);
  const rows = data.map((row) => headers.map((h) => `"${String(row[h] ?? "").replace(/"/g, '""')}"`).join(","));
  const csv = "\ufeff" + [headers.join(","), ...rows].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
  toast.success(`Exported ${data.length} records`);
}

/* ── Coupon Form ─────────────────────────────────────────── */
function CouponForm({ form, setForm, busy, onSubmit, onCancel }) {
  const d = (() => { try { return typeof form.data === "object" ? form.data : JSON.parse(form.data || "{}"); } catch { return {}; } })();
  const setD = (key, val) => { try { const cur = typeof form.data === "object" ? form.data : JSON.parse(form.data || "{}"); setForm({ ...form, data: JSON.stringify({ ...cur, [key]: val }) }); } catch { /* ignore */ } };
  return (
    <form onSubmit={onSubmit} className="resource-form">
      <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Coupon name (e.g. Diwali 2025)" required data-testid="coupons-title-input" />
      <input
        value={d.code || ""}
        onChange={(e) => setD("code", e.target.value.toUpperCase().replace(/\s/g, ""))}
        placeholder="Coupon Code (e.g. DIWALI20)"
        required
        data-testid="coupon-code-input"
        style={{ textTransform: "uppercase", letterSpacing: "2px", fontWeight: 700 }}
      />
      <div style={{ display: "flex", gap: 8 }}>
        <select value={d.discount_type || "percent"} onChange={(e) => setD("discount_type", e.target.value)} style={{ flex: 1 }} data-testid="coupon-type-select">
          <option value="percent">Percentage (%) Off</option>
          <option value="fixed">Fixed Amount (₹) Off</option>
        </select>
        <input
          type="number" min="0"
          max={d.discount_type === "percent" ? 100 : undefined}
          value={d.discount_value || ""}
          onChange={(e) => setD("discount_value", e.target.value)}
          placeholder={d.discount_type === "percent" ? "e.g. 20 (= 20% off)" : "e.g. 200 (= ₹200 off)"}
          required data-testid="coupon-value-input" style={{ flex: 1 }}
        />
      </div>
      <input type="number" min="0" value={d.minimum_order || ""} onChange={(e) => setD("minimum_order", e.target.value)} placeholder="Min Order ₹ — blank for no minimum" data-testid="coupon-min-order-input" />
      <select value={d.user_type || "all"} onChange={(e) => setD("user_type", e.target.value)} data-testid="coupon-user-type-select">
        <option value="all">Apply to All Users</option>
        <option value="specific">Specific User Only</option>
      </select>
      {d.user_type === "specific" && (
        <input
          value={(d.user_ids || []).join(", ")}
          onChange={(e) => setD("user_ids", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))}
          placeholder="User email (e.g. customer@gmail.com)"
          data-testid="coupon-user-ids-input"
        />
      )}
      <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="coupons-status-select">
        <option value="active">Active — Live on site</option>
        <option value="inactive">Inactive — Hidden</option>
      </select>
      <div style={{ display: "flex", gap: 8 }}>
        {onCancel && <button type="button" onClick={onCancel} style={{ flex: 1 }}><X size={14} /> Cancel</button>}
        <button className="primary-btn" disabled={busy} style={{ flex: 2 }} data-testid="save-coupons-button">
          {busy ? "Saving…" : <><Check size={14} /> {form.id ? "Update Coupon" : "Create Coupon"}</>}
        </button>
      </div>
    </form>
  );
}

/* ── Campaign Form ───────────────────────────────────────── */
function CampaignForm({ form, setForm, busy, onSubmit, onCancel }) {
  const d = (() => { try { return typeof form.data === "object" ? form.data : JSON.parse(form.data || "{}"); } catch { return {}; } })();
  const setD = (key, val) => { try { const cur = typeof form.data === "object" ? form.data : JSON.parse(form.data || "{}"); setForm({ ...form, data: JSON.stringify({ ...cur, [key]: val }) }); } catch { /* ignore */ } };
  return (
    <form onSubmit={onSubmit} className="resource-form">
      <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Campaign name (e.g. Mega Sale)" required data-testid="campaigns-title-input" />
      <input value={d.url || ""} onChange={(e) => setD("url", e.target.value)} placeholder="Campaign URL (e.g. https://dmmobile.in/sale)" data-testid="campaign-url-input" />
      <div style={{ display: "flex", gap: 8 }}>
        <div style={{ flex: 1 }}>
          <label style={{ fontSize: 11, opacity: 0.5, display: "block", marginBottom: 4 }}>Start Date</label>
          <input type="date" value={d.start_date || ""} onChange={(e) => setD("start_date", e.target.value)} data-testid="campaign-start-date" />
        </div>
        <div style={{ flex: 1 }}>
          <label style={{ fontSize: 11, opacity: 0.5, display: "block", marginBottom: 4 }}>End Date</label>
          <input type="date" value={d.end_date || ""} onChange={(e) => setD("end_date", e.target.value)} data-testid="campaign-end-date" />
        </div>
      </div>
      <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="campaigns-status-select">
        <option value="active">Active — Live</option>
        <option value="inactive">Inactive</option>
      </select>
      <div style={{ display: "flex", gap: 8 }}>
        {onCancel && <button type="button" onClick={onCancel} style={{ flex: 1 }}><X size={14} /> Cancel</button>}
        <button className="primary-btn" disabled={busy} style={{ flex: 2 }} data-testid="save-campaigns-button">
          {busy ? "Saving…" : <><Check size={14} /> {form.id ? "Update Campaign" : "Create Campaign"}</>}
        </button>
      </div>
    </form>
  );
}

/* ── Announcement Form ───────────────────────────────────── */
function AnnouncementForm({ form, setForm, busy, onSubmit, onCancel }) {
  return (
    <form onSubmit={onSubmit} className="resource-form">
      <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Announcement text (shows in ticker)" required data-testid="announcements-title-input" />
      <textarea value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Extra details (optional)" rows={2} data-testid="announcements-description-input" style={{ resize: "vertical" }} />
      <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="announcements-status-select">
        <option value="published">Published — Live ticker</option>
        <option value="active">Active</option>
        <option value="inactive">Hidden</option>
      </select>
      <div style={{ display: "flex", gap: 8 }}>
        {onCancel && <button type="button" onClick={onCancel} style={{ flex: 1 }}><X size={14} /> Cancel</button>}
        <button className="primary-btn" disabled={busy} style={{ flex: 2 }} data-testid="save-announcements-button">
          {busy ? "Saving…" : <><Check size={14} /> {form.id ? "Update" : "Publish Announcement"}</>}
        </button>
      </div>
    </form>
  );
}

/* ── Generic Form ─────────────────────────────────────────── */
function GenericForm({ resource, label, form, setForm, busy, onSubmit }) {
  return (
    <form onSubmit={onSubmit} className="resource-form">
      <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder={`${label} title`} required data-testid={`${resource}-title-input`} />
      <input value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Description (optional)" data-testid={`${resource}-description-input`} />
      <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid={`${resource}-status-select`}>
        <option value="active">Active</option>
        <option value="published">Published</option>
        <option value="inactive">Inactive</option>
      </select>
      <button className="primary-btn" disabled={busy} data-testid={`save-${resource}-button`}>
        {busy ? "Saving…" : <><Check size={14} /> {form.id ? "Update" : "Publish"}</>}
      </button>
    </form>
  );
}

const EMPTY = { id: "", title: "", description: "", status: "active", data: "{}" };

export default function AdminResourceManager({ resource, label, query = "" }) {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/api/admin/resources/${resource}?page_size=100`); setRows(data.items); }
    catch (error) { hindiError(apiError(error)); }
  }, [resource]);

  useEffect(() => { setForm(EMPTY); load(); }, [load]);

  const save = async (event) => {
    event.preventDefault();
    let dataObj;
    try { dataObj = typeof form.data === "object" ? form.data : JSON.parse(form.data || "{}"); }
    catch { hindiError("Data format is invalid"); return; }

    // Coupon: convert discount_type + discount_value → discount string backend expects
    if (resource === "coupons" && dataObj.discount_value) {
      dataObj = {
        ...dataObj,
        code: (dataObj.code || "").toUpperCase(),
        discount: (dataObj.discount_type || "percent") === "percent"
          ? `${dataObj.discount_value}%`
          : String(dataObj.discount_value),
        minimum_order: Number(dataObj.minimum_order || 0),
      };
    }

    setBusy(true);
    try {
      const payload = { title: form.title, description: form.description || "", status: form.status, data: dataObj };
      if (form.id) await api.patch(`/api/admin/resources/${resource}/${form.id}`, payload);
      else await api.post(`/api/admin/resources/${resource}`, payload);
      hindiFeedback(form.id ? `${label} updated` : `${label} published live`);
      setForm(EMPTY);
      await load();
    } catch (error) { hindiError(apiError(error)); } finally { setBusy(false); }
  };

  const edit = (row) => {
    const d = { ...(row.data || {}) };
    if (resource === "coupons" && d.discount) {
      const isPercent = String(d.discount).endsWith("%");
      d.discount_type = isPercent ? "percent" : "fixed";
      d.discount_value = isPercent ? String(d.discount).replace("%", "") : String(d.discount);
    }
    setForm({ id: row.id, title: row.title, description: row.description || "", status: row.status, data: JSON.stringify(d) });
  };

  const toggleLive = async (row) => {
    try {
      const newStatus = (row.status === "published" || row.status === "active") ? "inactive" : "published";
      await api.patch(`/api/admin/resources/${resource}/${row.id}`, { ...row, status: newStatus });
      hindiFeedback(newStatus === "inactive" ? "Removed from site" : "Now live on site");
      load();
    } catch (error) { hindiError(apiError(error)); }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete "${row.title}"?`)) return;
    try { await api.delete(`/api/admin/resources/${resource}/${row.id}`); hindiFeedback("Deleted"); load(); }
    catch (error) { hindiError(apiError(error)); }
  };

  const handleExport = () => {
    const csvData = rows.map((r) => ({
      Title: r.title,
      Status: r.status,
      ...(resource === "coupons" ? { Code: r.data?.code || "", Discount: r.data?.discount || "" } : {}),
      ...(resource === "campaigns" ? { URL: r.data?.url || "" } : {}),
      Updated: new Date(r.updated_at).toLocaleDateString("en-IN"),
    }));
    exportCSV(csvData, `${resource}-${new Date().toISOString().slice(0, 10)}.csv`);
  };

  const visible = filterRank(rows, query, [(row) => row.title, (row) => row.status]);

  const renderForm = () => {
    const cancel = form.id ? () => setForm(EMPTY) : undefined;
    if (resource === "coupons") return <CouponForm form={form} setForm={setForm} busy={busy} onSubmit={save} onCancel={cancel} />;
    if (resource === "campaigns") return <CampaignForm form={form} setForm={setForm} busy={busy} onSubmit={save} onCancel={cancel} />;
    if (resource === "announcements") return <AnnouncementForm form={form} setForm={setForm} busy={busy} onSubmit={save} onCancel={cancel} />;
    return <GenericForm resource={resource} label={label} form={form} setForm={setForm} busy={busy} onSubmit={save} />;
  };

  return (
    <div className="admin-manager" data-testid={`${resource}-manager`}>
      <section className="admin-panel resource-editor">
        <div className="panel-head">
          <div>
            <h2>{form.id ? `Edit ${label}` : `Add ${label}`}</h2>
            <span>Changes go live immediately after saving.</span>
          </div>
          {form.id && <button onClick={() => setForm(EMPTY)}><X size={14} /> Clear</button>}
        </div>
        {renderForm()}
      </section>
      <section className="admin-panel table-panel admin-wide">
        <div className="panel-head">
          <div>
            <h2>{label}</h2>
            <span data-testid={`${resource}-count`}>{rows.length} records</span>
          </div>
          {rows.length > 0 && (
            <button onClick={handleExport} data-testid={`export-${resource}-csv`} title="Download as CSV / Excel">
              <Download size={14} /> Export CSV
            </button>
          )}
        </div>
        {visible.length ? (
          <table data-testid={`${resource}-table`}>
            <thead>
              <tr>
                <th>Title</th>
                <th>Status</th>
                {resource === "coupons" && <th>Code</th>}
                {resource === "coupons" && <th>Discount</th>}
                {resource === "campaigns" && <th>Link</th>}
                <th>Updated</th>
                <th>Controls</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((row) => (
                <tr key={row.id}>
                  <td>
                    <b>{row.title}</b>
                    {row.description && <small>{row.description}</small>}
                  </td>
                  <td>
                    <span className={row.status === "active" || row.status === "published" ? "status-live" : "status-draft"}>
                      {row.status}
                    </span>
                  </td>
                  {resource === "coupons" && (
                    <td><code style={{ color: "#00d4ff", fontWeight: 700, letterSpacing: 1 }}>{row.data?.code || "—"}</code></td>
                  )}
                  {resource === "coupons" && (
                    <td>
                      {row.data?.discount
                        ? (String(row.data.discount).endsWith("%") ? `${row.data.discount} off` : `₹${row.data.discount} off`)
                        : "—"}
                    </td>
                  )}
                  {resource === "campaigns" && (
                    <td>
                      {row.data?.url
                        ? <a href={row.data.url} target="_blank" rel="noopener noreferrer" style={{ color: "#00d4ff", fontSize: 12 }}>View link</a>
                        : "—"}
                    </td>
                  )}
                  <td>{new Date(row.updated_at).toLocaleDateString("en-IN")}</td>
                  <td className="row-controls">
                    <button onClick={() => edit(row)} data-testid={`edit-${resource}-${row.id}`}><Pencil size={13} /> Edit</button>
                    <button onClick={() => toggleLive(row)} data-testid={`publish-${resource}-${row.id}`}>
                      {row.status === "active" || row.status === "published" ? "Unpublish" : "Go Live"}
                    </button>
                    <button onClick={() => remove(row)} data-testid={`delete-${resource}-${row.id}`}><Trash2 size={13} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="admin-empty-copy" data-testid={`${resource}-empty-state`}>No {label.toLowerCase()} yet. Add one above.</p>
        )}
      </section>
    </div>
  );
}
